import { useInfiniteQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';
import { useMemo } from 'react';

import { makeIsEnabled } from '@cast/utils';

import { useCurrentOrganizationId } from '@cast/infrastructure-organization';
import { apiClient } from '@cast/infrastructure-api';
import { useCluster } from '@cast/shared-cluster-context-data-access';

const PV_POLICIES_QUERY_KEY = 'pv-policies';

type UsePvPoliciesQueryArgs = {
  mode?: 'enabled' | 'dryRun';
  search?: string;
  enabled?: boolean;
  refetchInterval?: number;
};

export const getPvPoliciesQueryKey = ({
  organizationId,
  clusterId,
}: {
  organizationId?: string;
  clusterId?: string;
} = {}) => [PV_POLICIES_QUERY_KEY, organizationId, clusterId];

export const usePvPoliciesQuery = ({
  enabled = true,
  refetchInterval,
}: // TODO: expose filters to endpoint when it will be ready
UsePvPoliciesQueryArgs = {}) => {
  const organizationId = useCurrentOrganizationId();
  const { cluster } = useCluster();
  const clusterId = cluster?.id;

  const query = useInfiniteQuery({
    queryKey: getPvPoliciesQueryKey({ organizationId, clusterId }),
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.stop.listPvPolicies({
        organizationId,
        clusterId: clusterId,
        pageCursor: pageParam || undefined,
        pageLimit: '9999',
      });
      return data;
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) =>
      lastPage?.nextPageCursor ? lastPage.nextPageCursor : undefined,
    enabled: makeIsEnabled(!!organizationId, !!clusterId, enabled),
    refetchInterval,
  });

  const pvPolicies = useMemo(
    () => flatMap(query.data?.pages, (page) => page?.policies ?? []),
    [query.data]
  );

  return {
    ...query,
    pvPolicies,
    totalCount: query.data?.pages[0]?.totalCount,
  };
};
